<script setup>
import { ref, computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DrivePickerModal from 'dashboard/routes/dashboard/settings/integrations/Drives/components/DrivePickerModal.vue';

const emit = defineEmits(['close']);

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const selectedType = ref(null);
const selectedFile = ref(null);
const isCreating = ref(false);
const showPickerModal = ref(false);
const pickerDriveId = ref(null);

const accountId = computed(() => getters.getCurrentAccountId?.value);
const allDrives = computed(() => getters['drives/getDrives']?.value || []);
const pickerDrives = computed(() =>
  allDrives.value.filter(
    d =>
      d.status === 'active' &&
      ['google', 'onedrive', 'sharepoint'].includes(d.provider)
  )
);

const calendarTypes = [
  {
    id: 'spreadsheet',
    name: 'Spreadsheet',
    description: 'Connect a spreadsheet file from your drive',
    icon: 'i-lucide-file-spreadsheet',
  },
];

const canCreate = computed(() => {
  return selectedType.value && selectedFile.value;
});

const handleSelectType = type => {
  selectedType.value = type;
  selectedFile.value = null;
};

async function openPickerForDrive(drive) {
  if (!drive?.id) return;
  if (drive.provider === 'google') {
    try {
      const { useGooglePicker } = await import(
        'dashboard/composables/useGooglePicker'
      );
      const picker = useGooglePicker();
      await picker.buildAndShowPickerInPlace({
        driveId: drive.id,
        filterType: 'spreadsheet',
        onSelect: files => {
          const f = Array.isArray(files) ? files[0] : files;
          if (f)
            selectedFile.value = {
              ...f,
              drive_id: drive.id,
              provider: 'google',
            };
        },
        onCancel: () => {},
      });
    } catch (err) {
      useAlert(err?.message || 'Failed to open Google Picker');
    }
    return;
  }
  pickerDriveId.value = drive.id;
  showPickerModal.value = true;
}

function onPickerSelect(fileOrFiles) {
  const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
  if (file) selectedFile.value = file;
  showPickerModal.value = false;
}

function onPickerCancel() {
  showPickerModal.value = false;
}

defineExpose({
  handleFileSelected: file => {
    selectedFile.value = file;
  },
});

const handleCreate = async () => {
  if (!canCreate.value) return;

  isCreating.value = true;
  try {
    const settings = {
      calendar_type: selectedType.value,
      drive_id: selectedFile.value.drive_id,
      file_id: selectedFile.value.id,
      file_name: selectedFile.value.name,
      file_type: selectedFile.value.mime_type || null,
      provider: selectedFile.value.provider,
    };
    if (selectedFile.value.source_hint) {
      settings.file_source_hint = selectedFile.value.source_hint;
    }
    if (selectedFile.value.graph_drive_id) {
      settings.file_graph_drive_id = selectedFile.value.graph_drive_id;
    }
    await store.dispatch('calendars/create', {
      settings,
      status: 'active',
    });
    useAlert(t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.SUCCESS'));
    emit('close');
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    class="w-[30rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] p-6 rounded-xl border border-slate-50 dark:border-slate-900 shadow-md flex flex-col gap-6 max-h-[85vh] overflow-y-auto"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-slate-900 dark:text-slate-50">
        {{ $t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.TITLE') }}
      </h3>
      <button
        class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        @click="handleClose"
      >
        <Icon icon="i-lucide-x" class="w-5 h-5" />
      </button>
    </div>

    <p class="text-sm text-slate-600 dark:text-slate-400">
      {{ $t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.DESCRIPTION') }}
    </p>

    <!-- Step 1: Select Calendar Type -->
    <div class="space-y-3">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ $t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.SELECT_TYPE') }}
      </label>
      <div
        v-for="type in calendarTypes"
        :key="type.id"
        class="flex items-start p-4 border rounded-lg cursor-pointer transition-colors"
        :class="[
          selectedType === type.id
            ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
        ]"
        @click="handleSelectType(type.id)"
      >
        <input
          type="radio"
          :value="type.id"
          :checked="selectedType === type.id"
          class="mr-3 mt-1"
        />
        <Icon
          :icon="type.icon"
          class="w-6 h-6 mr-3 mt-0.5 text-slate-700 dark:text-slate-300"
        />
        <div class="flex-1">
          <div class="font-medium text-slate-900 dark:text-slate-100 text-sm">
            {{ type.name }}
          </div>
          <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{ type.description }}
          </div>
        </div>
        <Icon
          v-if="selectedType === type.id"
          icon="i-lucide-check-circle"
          class="w-5 h-5 text-woot-500 mt-0.5"
        />
      </div>
    </div>

    <!-- Step 2: Select File -->
    <div v-if="selectedType" class="space-y-3">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ $t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.SELECT_FILE') }}
      </label>

      <div
        v-if="selectedFile"
        class="flex items-center gap-3 px-4 py-3 border border-woot-500 rounded-lg bg-woot-50 dark:bg-woot-900/20"
      >
        <Icon
          icon="i-lucide-file-spreadsheet"
          class="w-5 h-5 text-woot-600 dark:text-woot-400 flex-shrink-0"
        />
        <span
          class="font-medium text-slate-900 dark:text-slate-100 text-sm truncate flex-1"
        >
          {{ selectedFile.name }}
        </span>
        <span
          class="px-2 py-0.5 bg-woot-100 dark:bg-woot-800 rounded text-xs text-woot-700 dark:text-woot-300"
        >
          {{ selectedFile.provider }}
        </span>
        <button
          type="button"
          class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          @click="selectedFile = null"
        >
          <Icon icon="i-lucide-x-circle" class="w-5 h-5" />
        </button>
      </div>

      <div v-else class="space-y-2">
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Pick a spreadsheet from one of your connected drives:
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="drive in pickerDrives"
            :key="drive.id"
            size="sm"
            variant="outline"
            slate
            icon="i-lucide-folder-open"
            @click="openPickerForDrive(drive)"
          >
            {{
              drive.name ||
              $t(
                `INTEGRATION_SETTINGS.DRIVES.PROVIDERS.${drive.provider?.toUpperCase()}`
              )
            }}
            – Pick a file
          </Button>
        </div>
        <p
          v-if="pickerDrives.length === 0"
          class="text-xs text-slate-500 dark:text-slate-400"
        >
          No compatible drives connected. Connect Google Drive, OneDrive, or
          SharePoint first.
        </p>
      </div>
    </div>

    <DrivePickerModal
      v-model:show="showPickerModal"
      :drive-id="pickerDriveId"
      filter-type="spreadsheet"
      @select="onPickerSelect"
      @cancel="onPickerCancel"
    />

    <!-- Footer Actions -->
    <div class="flex items-center justify-end gap-3 pt-4 border-t">
      <Button
        :label="$t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.CANCEL')"
        variant="clear"
        @click="handleClose"
      />
      <Button
        :label="
          isCreating
            ? $t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.CREATING')
            : $t('INTEGRATION_SETTINGS.CALENDARS.ADD_MODAL.CREATE')
        "
        :disabled="!canCreate || isCreating"
        @click="handleCreate"
      />
    </div>
  </div>
</template>
